<!--
  Newsletters.svelte — Newsletter campaign management page.

  Lists all newsletters with create/edit/delete, send/pause/resume/cancel
  controls, preview, test-send, and analytics drill-down.
-->
<script lang="ts">
	import { run } from 'svelte/legacy';

	import { onMount, onDestroy } from 'svelte';
	import { push } from '../../abstract-spa-router';
	import {
		Table,
		TableHead,
		TableHeadCell,
		TableBody,
		TableBodyRow,
		TableBodyCell,
		Badge,
		Button,
		Input,
		Spinner,
		Modal,
		Toggle,
		Heading,
		Progressbar
	} from 'flowbite-svelte';
	import AbstractCard from '../../components/AbstractCard.svelte';
	import AbstractDropdown from '../../components/AbstractDropdown.svelte';
	import {
		listNewsletters,
		deleteNewsletter,
		pauseNewsletter,
		resumeNewsletter,
		cancelNewsletter,
		getNewsletterAnalytics,
		listNewsletterRecipients,
		previewNewsletter,
		testSendNewsletter,
		listEmailTemplates
	} from '../../AppNetwork';
	import type {
		Newsletter,
		NewsletterAnalytics,
		NewsletterRecipient,
		NewsletterStatus,
		EmailTemplate,
		SortDirection
	} from '../../AppTypes';
	import { toastSuccess, toastError } from '../../toast';
	import { ConfirmButton, SortableTableHeadCell } from '../../components';
	import PageHeader from '../../components/PageHeader.svelte';
	import { t, t2 } from '../../i18n';

	type BadgeColor = 'red' | 'yellow' | 'green' | 'indigo' | 'purple' | 'blue' | 'gray' | 'primary';

	/** Loaded newsletters. */
	let newsletters: Newsletter[] = $state([]);
	/** Total count for pagination. */
	let total = $state(0);
	/** Current offset. */
	let offset = $state(0);
	/** Page size. */
	const limit = 20;
	/** Loading flag. */
	let loading = $state(true);
	/** Error string. */
	let error = $state('');
	/** Search input. */
	let search = $state('');
	/** Status filter. */
	let statusFilter = $state('');

	// ── Sorting ───────────────────────────────────────────────────

	/** Active server-side sort key for the campaign list (`''` = newest first). */
	let sortBy = $state('');
	/** Active server-side sort direction for the campaign list. */
	let sortDir: SortDirection = $state('desc');

	/** Switch the campaign sort column/direction, reset paging, and refetch. */
	function handleSort(key: string, dir: SortDirection): void {
		sortBy = key;
		sortDir = dir;
		offset = 0;
		loadNewsletters();
	}

	/** Active server-side sort key for the recipient list (`''` = insertion order). */
	let recipientSortBy = $state('');
	/** Active server-side sort direction for the recipient list. */
	let recipientSortDir: SortDirection = $state('asc');

	/** Switch the recipient sort column/direction and refetch that list. */
	function handleRecipientSort(key: string, dir: SortDirection): void {
		recipientSortBy = key;
		recipientSortDir = dir;
		loadRecipients();
	}

	// ── Preview ───────────────────────────────────────────────────

	/** Show the preview modal. */
	let showPreview = $state(false);
	/** Newsletter being previewed. */
	let previewingNl: Newsletter | null = $state(null);
	/** Rendered preview HTML. */
	let previewHtml = $state('');
	/** Preview loading flag. */
	let previewLoading = $state(false);
	/** Dark-mode preview toggle. */
	let previewDarkMode = $state(false);
	/** Master template override for preview. */
	let previewMasterTemplateId: number | string = $state('');
	/** Available master templates for the dropdown. */
	let masterTemplates: EmailTemplate[] = $state([]);

	// ── Test Send ─────────────────────────────────────────────────

	/** Test-send in progress flag. */
	let testSending = $state(false);

	// ── Delete ────────────────────────────────────────────────────

	/** Execute the deletion of a newsletter. */
	async function performDelete(nl: Newsletter): Promise<void> {
		try {
			await deleteNewsletter(nl.id);
			toastSuccess(t2('Newsletter deleted.'));
			await loadNewsletters();
		} catch (e) {
			toastError(e instanceof Error ? e.message : t2('Failed to delete newsletter'));
		}
	}

	// ── Analytics ─────────────────────────────────────────────────

	let showAnalytics = $state(false);
	let analyticsLoading = $state(false);
	let analytics: NewsletterAnalytics | null = $state(null);
	let analyticsNl: Newsletter | null = $state(null);
	let recipients: NewsletterRecipient[] = $state([]);
	let recipientTotal = $state(0);

	// ── Status options ────────────────────────────────────────────

	/** Status filter dropdown options. */
	const statusOptions = [
		{ value: '', name: t2('All Statuses'), icon: 'fa-solid fa-list' },
		{ value: 'DRAFT', name: t2('Draft'), icon: 'fa-solid fa-file-pen' },
		{ value: 'SCHEDULED', name: t2('Scheduled'), icon: 'fa-solid fa-clock' },
		{ value: 'SENDING', name: t2('Sending'), icon: 'fa-solid fa-paper-plane' },
		{ value: 'SENT', name: t2('Sent'), icon: 'fa-solid fa-circle-check' },
		{ value: 'PAUSED', name: t2('Paused'), icon: 'fa-solid fa-pause' },
		{ value: 'CANCELLED', name: t2('Cancelled'), icon: 'fa-solid fa-ban' },
		{ value: 'FAILED', name: t2('Failed'), icon: 'fa-solid fa-circle-xmark' }
	];

	// ── Badge colours per status ──────────────────────────────────

	/** Returns badge colour for a newsletter status. */
	function statusColor(status: NewsletterStatus): BadgeColor {
		switch (status) {
			case 'DRAFT':
				return 'gray';
			case 'SCHEDULED':
				return 'indigo';
			case 'SENDING':
				return 'yellow';
			case 'SENT':
				return 'green';
			case 'PAUSED':
				return 'purple';
			case 'CANCELLED':
				return 'red';
			case 'FAILED':
				return 'red';
			default:
				return 'gray';
		}
	}

	// ── Data loading ──────────────────────────────────────────────

	/** Loads the newsletter list from the server. */
	async function loadNewsletters(): Promise<void> {
		loading = true;
		error = '';
		/** Fetch newsletters from server and handle failures gracefully. */
		try {
			const result = await listNewsletters({
				limit,
				offset,
				status: statusFilter || undefined,
				search: search || undefined,
				sortBy: sortBy || undefined,
				sortDir: sortBy ? sortDir : undefined
			});
			newsletters = result.items;
			total = result.total;
		} catch (e) {
			error = e instanceof Error ? e.message : t2('Failed to load newsletters');
		} finally {
			loading = false;
		}
	}

	// ── Pagination ────────────────────────────────────────────────

	/** Navigate to next page. */
	function nextPage(): void {
		if (offset + limit < total) {
			offset += limit;
			loadNewsletters();
		}
	}

	/** Navigate to previous page. */
	function prevPage(): void {
		if (offset > 0) {
			offset = Math.max(0, offset - limit);
			loadNewsletters();
		}
	}

	// ── Search / Filter ───────────────────────────────────────────

	/** Reset offset and reload when filters change. */
	function applyFilter(): void {
		offset = 0;
		loadNewsletters();
	}

	// ── Create / Edit ─────────────────────────────────────────────

	/** Navigates to the full-page newsletter editor (create mode). */
	function openCreate(): void {
		push('/Newsletters/new');
	}

	/** Navigates to the full-page newsletter editor (edit mode). */
	function openEdit(nl: Newsletter): void {
		push(`/Newsletters/${nl.id}/edit`);
	}

	// ── Delete ────────────────────────────────────────────────────

	// ── Campaign Actions ──────────────────────────────────────────

	/** Navigates to the pre-send staging page for the newsletter. */
	function doSend(nl: Newsletter): void {
		push(`/Newsletters/${nl.id}/send`);
	}

	/** Pauses a sending newsletter. */
	async function doPause(nl: Newsletter): Promise<void> {
		try {
			await pauseNewsletter(nl.id);
			toastSuccess(t2('Newsletter paused.'));
			await loadNewsletters();
		} catch (e) {
			toastError(e instanceof Error ? e.message : t2('Failed to pause newsletter'));
		}
	}

	/** Resumes a paused newsletter. */
	async function doResume(nl: Newsletter): Promise<void> {
		try {
			await resumeNewsletter(nl.id);
			toastSuccess(t2('Newsletter resumed.'));
			await loadNewsletters();
		} catch (e) {
			toastError(e instanceof Error ? e.message : t2('Failed to resume newsletter'));
		}
	}

	/** Cancels a newsletter. */
	async function doCancel(nl: Newsletter): Promise<void> {
		try {
			await cancelNewsletter(nl.id);
			toastSuccess(t2('Newsletter cancelled.'));
			await loadNewsletters();
		} catch (e) {
			toastError(e instanceof Error ? e.message : t2('Failed to cancel newsletter'));
		}
	}

	// ── Analytics ─────────────────────────────────────────────────

	/** Opens analytics for a newsletter. */
	async function openAnalytics(nl: Newsletter): Promise<void> {
		analyticsNl = nl;
		analyticsLoading = true;
		showAnalytics = true;
		analytics = null;
		recipients = [];
		recipientTotal = 0;
		/** Load analytics data and first page of recipients for this newsletter. */
		try {
			analytics = await getNewsletterAnalytics(nl.id);
			await loadRecipients();
		} catch (e) {
			error = e instanceof Error ? e.message : t2('Failed to load analytics');
		} finally {
			analyticsLoading = false;
		}
	}

	/** Loads the first page of recipients for the newsletter shown in the analytics modal. */
	async function loadRecipients(): Promise<void> {
		if (!analyticsNl) return;

		const recipientResult = await listNewsletterRecipients(analyticsNl.id, {
			limit: 20,
			offset: 0,
			sortBy: recipientSortBy || undefined,
			sortDir: recipientSortBy ? recipientSortDir : undefined
		});
		recipients = recipientResult.items;
		recipientTotal = recipientResult.total;
	}

	// ── Preview ──────────────────────────────────────────────────

	/** Opens the preview modal for a newsletter. */
	async function openPreview(nl: Newsletter): Promise<void> {
		previewingNl = nl;
		previewDarkMode = false;
		previewMasterTemplateId = '';
		previewHtml = '';
		showPreview = true;
		await refreshPreview(nl.id);
	}

	/** Refreshes the preview with potentially different master template. */
	async function refreshPreview(id?: number): Promise<void> {
		const nlId = id ?? previewingNl?.id;
		/** Bail out if no newsletter ID is available. */
		if (!nlId) return;
		previewLoading = true;
		/** Fetch rendered preview HTML, optionally with overridden master template. */
		try {
			const masterId = previewMasterTemplateId ? Number(previewMasterTemplateId) : undefined;
			const result = await previewNewsletter(nlId, masterId);
			previewHtml = result.html;
		} catch (e) {
			toastError(e instanceof Error ? e.message : t2('Failed to load preview'));
		} finally {
			previewLoading = false;
		}
	}

	/** Sends a test email of the newsletter to the current user. */
	async function doTestSend(nl: Newsletter): Promise<void> {
		testSending = true;
		/** Send a test email and report the outcome to the user. */
		try {
			const result = await testSendNewsletter(nl.id);
			toastSuccess(result.message);
		} catch (e) {
			toastError(e instanceof Error ? e.message : t2('Failed to send test email'));
		} finally {
			testSending = false;
		}
	}

	// ── Live countdown / auto-refresh for SENDING newsletters ────

	/** Interval ID for the 1-second countdown ticker. */
	let tickInterval: ReturnType<typeof setInterval> | null = null;
	/** Interval ID for the data auto-refresh (every 15 s). */
	let refreshInterval: ReturnType<typeof setInterval> | null = null;
	/** Current timestamp updated every second for reactive countdowns. */
	let now = $state(Date.now());

	/** Starts the 1-second tick and 15-second data refresh. */
	function startTimers(): void {
		if (!tickInterval) {
			tickInterval = setInterval(() => {
				now = Date.now();
			}, 1000);
		}
		/** Start periodic data refresh so the list stays current while sending. */
		if (!refreshInterval) {
			refreshInterval = setInterval(() => {
				loadNewsletters();
			}, 15000);
		}
	}

	/** Stops all timers. */
	function stopTimers(): void {
		if (tickInterval) {
			clearInterval(tickInterval);
			tickInterval = null;
		}
		/** Clear the data-refresh interval as well. */
		if (refreshInterval) {
			clearInterval(refreshInterval);
			refreshInterval = null;
		}
	}

	/**
	 * Computes the seconds remaining until the next wave for a SENDING newsletter.
	 * Returns 0 if the newsletter is not sending or the time has passed.
	 */
	function secondsUntilNextWave(nl: Newsletter, _now: number): number {
		if (nl.status !== 'SENDING' || !nl.lastWaveAt || nl.currentWave >= nl.totalWaves) return 0;
		const nextAt = new Date(nl.lastWaveAt).getTime() + nl.waveIntervalSeconds * 1000;
		return Math.max(0, Math.ceil((nextAt - _now) / 1000));
	}

	/**
	 * Formats seconds into a human-readable countdown string like "1m 23s".
	 */
	function formatCountdown(seconds: number): string {
		if (seconds <= 0) return 'now';
		const m = Math.floor(seconds / 60);
		const s = seconds % 60;
		return m > 0 ? `${m}m ${s.toString().padStart(2, '0')}s` : `${s}s`;
	}

	// ── Lifecycle ─────────────────────────────────────────────────

	onMount(async () => {
		await loadNewsletters();
		/** Load master templates for the preview dropdown (non-critical). */
		try {
			const templates = await listEmailTemplates();
			masterTemplates = templates.filter((t) => t.templateType === 'master');
		} catch {
			/* Master template list is non-critical; swallow errors. */
		}
	});

	onDestroy(() => {
		stopTimers();
	});
	/** Preview dropdown options (inherit + available masters). */
	let previewMasterOptions = $derived([
		{ value: '', name: t2('Newsletter default'), icon: 'fa-solid fa-ban' },
		...masterTemplates.map((t) => ({ value: String(t.id), name: t.templateName, icon: 'fa-solid fa-file-code' }))
	]);
	/** Computed srcdoc for preview iframe (injects dark-mode styles). */
	let previewSrcdoc = $derived(previewDarkMode
		? `<style>html,body{background:#1e1e1e!important;color:#d4d4d4!important;color-scheme:dark}img{filter:brightness(.85)}</style>${previewHtml}`
		: previewHtml);
	/** True when at least one newsletter is actively sending. */
	let hasSending = $derived(newsletters.some((nl) => nl.status === 'SENDING'));
	/** Start or stop timers based on whether any newsletter is sending. */
	run(() => {
		if (hasSending) {
			startTimers();
		} else {
			stopTimers();
		}
	});
</script>

<!-- Header -->
<PageHeader title={$t("Newsletters")} description={$t("Create, manage and send newsletter campaigns.")} />

{#if error}
	<div class="mb-4 rounded-lg bg-red-100 p-3 text-sm text-red-700 dark:bg-red-900 dark:text-red-300">
		{error}
		<button class="ml-2 underline" onclick={() => (error = '')}>{$t("dismiss")}</button>
	</div>
{/if}

<!-- Toolbar -->
<div class="mb-4 flex flex-wrap items-center gap-3">
	<Input
		placeholder={$t("Search name or subject…")}
		bind:value={search}
		onkeyup={(e: KeyboardEvent) => {
			if (e.key === 'Enter') applyFilter();
		}}
		class="max-w-xs"
	/>
	<AbstractDropdown items={statusOptions} bind:value={statusFilter} onchange={() => applyFilter()} class="w-44" />
	<Button size="sm" onclick={applyFilter}>
		<i class="fa-solid fa-magnifying-glass mr-1 h-4 w-4"></i> {$t("Search")}
	</Button>
	<div class="flex-grow"></div>
	<Button size="sm" onclick={openCreate}>
		<i class="fa-solid fa-plus mr-1 h-4 w-4"></i> {$t("New Newsletter")}
	</Button>
</div>

<!-- Table -->
{#if loading}
	<div class="flex justify-center py-12"><Spinner /></div>
{:else if newsletters.length === 0}
	<AbstractCard class="max-w-none">
		<p class="text-center text-gray-500 dark:text-gray-400">{$t("No newsletters found.")}</p>
	</AbstractCard>
{:else}
	<Table divClass="widget !p-1 relative" class="dataTable">
		<TableHead>
			<SortableTableHeadCell key="name" {sortBy} {sortDir} onsort={handleSort}>{$t("Name")}</SortableTableHeadCell>
			<SortableTableHeadCell key="subject" {sortBy} {sortDir} onsort={handleSort}>
				{$t("Subject")}
			</SortableTableHeadCell>
			<SortableTableHeadCell key="status" {sortBy} {sortDir} onsort={handleSort}>{$t("Status")}</SortableTableHeadCell>
			<SortableTableHeadCell key="recipients" {sortBy} {sortDir} defaultDir="desc" onsort={handleSort}>
				{$t("Recipients")}
			</SortableTableHeadCell>
			<SortableTableHeadCell key="sent" {sortBy} {sortDir} defaultDir="desc" onsort={handleSort}>
				{$t("Sent")}
			</SortableTableHeadCell>
			<SortableTableHeadCell key="opens" {sortBy} {sortDir} defaultDir="desc" onsort={handleSort}>
				{$t("Opens")}
			</SortableTableHeadCell>
			<SortableTableHeadCell key="created" {sortBy} {sortDir} defaultDir="desc" onsort={handleSort}>
				{$t("Created")}
			</SortableTableHeadCell>
			<TableHeadCell class="text-right">{$t("Actions")}</TableHeadCell>
		</TableHead>
		<TableBody class="divide-y divide-gray-200 dark:divide-gray-700">
			{#each newsletters as nl (nl.id)}
				<TableBodyRow class="hover:bg-gray-50 dark:hover:bg-gray-600">
					<TableBodyCell>{nl.name}</TableBodyCell>
					<TableBodyCell class="max-w-[200px] truncate">{nl.subject}</TableBodyCell>
					<TableBodyCell>
						<Badge color={statusColor(nl.status)}>{nl.status}</Badge>
						{#if nl.status === 'SENDING'}
							{@const remaining = secondsUntilNextWave(nl, now)}
							<div class="mt-1.5 min-w-[140px] space-y-1">
								<div class="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
									<span>{$t("Wave")} {nl.currentWave}/{nl.totalWaves}</span>
									{#if nl.currentWave < nl.totalWaves}
										<span class="font-mono tabular-nums">{formatCountdown(remaining)}</span>
									{/if}
								</div>
								<Progressbar
									progress={nl.totalWaves > 0
										? Math.round((nl.currentWave / nl.totalWaves) * 100)
										: 0}
									size="h-1.5"
									color="yellow"
								/>
							</div>
						{/if}
					</TableBodyCell>
					<TableBodyCell>{nl.totalRecipients}</TableBodyCell>
					<TableBodyCell>{nl.sentCount}</TableBodyCell>
					<TableBodyCell>{nl.openCount}</TableBodyCell>
					<TableBodyCell>{new Date(nl.created).toLocaleDateString()}</TableBodyCell>
					<TableBodyCell class="whitespace-nowrap text-right">
						<!-- Preview -->
						<Button size="xs" color="alternative" onclick={() => openPreview(nl)} title={$t("Preview")}>
							<i class="fa-solid fa-eye h-3.5 w-3.5"></i>
						</Button>

						<!-- Test Send -->
						<Button
							size="xs"
							color="alternative"
							onclick={() => doTestSend(nl)}
							title={$t("Send test email")}
							disabled={testSending}
						>
							<i class="fa-solid fa-flask h-3.5 w-3.5"></i>
						</Button>

						<!-- Analytics -->
						{#if nl.status !== 'DRAFT'}
							<Button size="xs" color="alternative" onclick={() => openAnalytics(nl)} title={$t("Analytics")}>
								<i class="fa-solid fa-chart-simple h-3.5 w-3.5"></i>
							</Button>
						{/if}

						<!-- Edit (only DRAFT / SCHEDULED) -->
						{#if nl.status === 'DRAFT' || nl.status === 'SCHEDULED'}
							<Button size="xs" color="alternative" onclick={() => openEdit(nl)} title={$t("Edit")}>
								<i class="fa-solid fa-pen h-3.5 w-3.5"></i>
							</Button>
						{/if}

						<!-- Send (DRAFT / SCHEDULED) — navigates to staging page -->
						{#if nl.status === 'DRAFT' || nl.status === 'SCHEDULED'}
							<Button size="xs" color="green" onclick={() => doSend(nl)} title={$t("Send")}>
								<i class="fa-solid fa-paper-plane h-3.5 w-3.5"></i>
							</Button>
						{/if}

						<!-- Pause (SENDING) -->
						{#if nl.status === 'SENDING'}
							<Button size="xs" color="yellow" onclick={() => doPause(nl)} title={$t("Pause")}>
								<i class="fa-solid fa-pause h-3.5 w-3.5"></i>
							</Button>
						{/if}

						<!-- Resume (PAUSED) -->
						{#if nl.status === 'PAUSED'}
							<Button size="xs" color="green" onclick={() => doResume(nl)} title={$t("Resume")}>
								<i class="fa-solid fa-play h-3.5 w-3.5"></i>
							</Button>
						{/if}

						<!-- Cancel (SENDING / PAUSED) -->
						{#if nl.status === 'SENDING' || nl.status === 'PAUSED'}
							<ConfirmButton
								size="xs"
								color="red"
								action={$t("cancel this newsletter")}
								onconfirm={() => doCancel(nl)}
							>
								<i class="fa-solid fa-xmark h-3.5 w-3.5"></i>
							</ConfirmButton>
						{/if}

						<!-- Delete -->
						{#if nl.status !== 'SENDING' && nl.status !== 'PAUSED'}
							<ConfirmButton
								size="xs"
								color="red"
								outline
								action={$t("delete this newsletter")}
								onconfirm={() => performDelete(nl)}
							>
								<i class="fa-solid fa-trash h-3.5 w-3.5"></i>
							</ConfirmButton>
						{/if}
					</TableBodyCell>
				</TableBodyRow>
			{/each}
		</TableBody>
	</Table>

	<!-- Pagination -->
	<div class="mt-4 flex items-center justify-between text-sm text-gray-500 dark:text-gray-400">
		<span>{$t("Showing")} {offset + 1}–{Math.min(offset + limit, total)} {$t("of")} {total}</span>
		<div class="flex gap-2">
			<Button size="xs" color="alternative" disabled={offset === 0} onclick={prevPage}>{$t("Previous")}</Button>
			<Button size="xs" color="alternative" disabled={offset + limit >= total} onclick={nextPage}>{$t("Next")}</Button>
		</div>
	</div>
{/if}

<!-- Analytics Modal -->
<Modal
	title={analyticsNl ? `${$t('Analytics')}: ${analyticsNl.name}` : $t('Analytics')}
	bind:open={showAnalytics}
	size="xl"
	outsideclose
>
	{#if analyticsLoading}
		<div class="flex justify-center py-8"><Spinner /></div>
	{:else if analytics}
		<!-- Summary cards -->
		<div class="mb-6 grid grid-cols-3 gap-4">
			<AbstractCard class="max-w-none text-center">
				<div class="text-2xl font-bold">{analytics.totalRecipients}</div>
				<div class="text-sm text-gray-500 dark:text-gray-400">{$t("Recipients")}</div>
			</AbstractCard>
			<AbstractCard class="max-w-none text-center">
				<div class="text-2xl font-bold">{analytics.sentCount}</div>
				<div class="text-sm text-gray-500 dark:text-gray-400">{$t("Sent")}</div>
			</AbstractCard>
			<AbstractCard class="max-w-none text-center">
				<div class="text-2xl font-bold text-red-600">{analytics.failedCount}</div>
				<div class="text-sm text-gray-500 dark:text-gray-400">{$t("Failed")}</div>
			</AbstractCard>
			<AbstractCard class="max-w-none text-center">
				<div class="text-2xl font-bold text-blue-600">{analytics.openRate}%</div>
				<div class="text-sm text-gray-500 dark:text-gray-400">{$t("Open Rate")} ({analytics.openCount})</div>
			</AbstractCard>
			<AbstractCard class="max-w-none text-center">
				<div class="text-2xl font-bold text-green-600">{analytics.clickRate}%</div>
				<div class="text-sm text-gray-500 dark:text-gray-400">{$t("Click Rate")} ({analytics.clickCount})</div>
			</AbstractCard>
			<AbstractCard class="max-w-none text-center">
				<div class="text-2xl font-bold text-orange-600">{analytics.unsubscribeCount}</div>
				<div class="text-sm text-gray-500 dark:text-gray-400">{$t("Unsubscribes")}</div>
			</AbstractCard>
		</div>

		<!-- Top URLs -->
		{#if analytics.topUrls.length > 0}
			<Heading tag="h5" class="mb-2">{$t("Top Clicked URLs")}</Heading>
			<Table divClass="widget !p-1 relative" class="dataTable">
				<TableHead>
					<TableHeadCell>{$t("URL")}</TableHeadCell>
					<TableHeadCell class="text-right">{$t("Clicks")}</TableHeadCell>
				</TableHead>
				<TableBody class="divide-y divide-gray-200 dark:divide-gray-700">
					{#each analytics.topUrls as link (link.url)}
						<TableBodyRow class="hover:bg-gray-50 dark:hover:bg-gray-600">
							<TableBodyCell class="max-w-[400px] truncate">
								<a
									href={link.url}
									target="_blank"
									rel="noopener noreferrer"
									class="text-blue-600 hover:underline">{link.url}</a
								>
							</TableBodyCell>
							<TableBodyCell class="text-right font-medium">{link.clicks}</TableBodyCell>
						</TableBodyRow>
					{/each}
				</TableBody>
			</Table>
		{/if}

		<!-- Recipient list -->
		{#if recipients.length > 0}
			<Heading tag="h5" class="mb-2 mt-6">{$t("Recipients")} ({recipientTotal})</Heading>
			<Table divClass="widget !p-1 relative" class="dataTable">
				<TableHead>
					<SortableTableHeadCell
						key="email"
						sortBy={recipientSortBy}
						sortDir={recipientSortDir}
						onsort={handleRecipientSort}>{$t("Email")}</SortableTableHeadCell
					>
					<SortableTableHeadCell
						key="name"
						sortBy={recipientSortBy}
						sortDir={recipientSortDir}
						onsort={handleRecipientSort}>{$t("Name")}</SortableTableHeadCell
					>
					<SortableTableHeadCell
						key="status"
						sortBy={recipientSortBy}
						sortDir={recipientSortDir}
						onsort={handleRecipientSort}>{$t("Status")}</SortableTableHeadCell
					>
					<SortableTableHeadCell
						key="opens"
						sortBy={recipientSortBy}
						sortDir={recipientSortDir}
						defaultDir="desc"
						onsort={handleRecipientSort}>{$t("Opens")}</SortableTableHeadCell
					>
					<TableHeadCell>{$t("Clicks")}</TableHeadCell>
					<SortableTableHeadCell
						key="sentAt"
						sortBy={recipientSortBy}
						sortDir={recipientSortDir}
						defaultDir="desc"
						onsort={handleRecipientSort}>{$t("Sent At")}</SortableTableHeadCell
					>
				</TableHead>
				<TableBody class="divide-y divide-gray-200 dark:divide-gray-700">
					{#each recipients as r (r.id)}
						<TableBodyRow class="hover:bg-gray-50 dark:hover:bg-gray-600">
							<TableBodyCell>{r.email}</TableBodyCell>
							<TableBodyCell>{[r.firstName, r.lastName].filter(Boolean).join(' ') || '—'}</TableBodyCell>
							<TableBodyCell
								><Badge
									color={r.status === 'FAILED'
										? 'red'
										: r.status === 'SENT'
											? 'green'
											: r.status === 'OPENED'
												? 'blue'
												: r.status === 'CLICKED'
													? 'indigo'
													: r.status === 'UNSUBSCRIBED'
														? 'purple'
														: 'gray'}>{r.status}</Badge
								></TableBodyCell
							>
							<TableBodyCell>{r.openCount}</TableBodyCell>
							<TableBodyCell>
								{#if r.clickEvents && r.clickEvents.length > 0}
									<div class="space-y-0.5">
										{#each r.clickEvents as evt (evt.id)}
											<div class="max-w-[250px] truncate text-xs">
												<a
													href={evt.originalUrl}
													target="_blank"
													rel="noopener noreferrer"
													class="text-blue-600 hover:underline">{evt.originalUrl}</a
												>
												<span class="ml-1 text-gray-400"
													>{new Date(evt.created).toLocaleString()}</span
												>
											</div>
										{/each}
									</div>
								{:else}
									<span class="text-xs text-gray-400">&mdash;</span>
								{/if}
							</TableBodyCell>
							<TableBodyCell>{r.sentAt ? new Date(r.sentAt).toLocaleString() : '—'}</TableBodyCell>
						</TableBodyRow>
					{/each}
				</TableBody>
			</Table>
		{/if}
	{/if}
</Modal>

<!-- Preview Modal -->
<Modal
	title={previewingNl ? `${$t('Preview')}: ${previewingNl.name}` : $t('Preview')}
	bind:open={showPreview}
	size="xl"
	outsideclose
>
	<!-- Controls bar -->
	<div class="mb-4 flex flex-wrap items-center gap-3">
		<AbstractDropdown items={previewMasterOptions} bind:value={previewMasterTemplateId} onchange={() => refreshPreview()} class="max-w-[14rem]" />
		<Toggle bind:checked={previewDarkMode} class="ml-2">{$t("Dark Mode")}</Toggle>
		<Button size="xs" color="alternative" onclick={() => refreshPreview()} disabled={previewLoading}>
			<i class="fa-solid fa-arrows-rotate mr-1 h-3.5 w-3.5"></i> {$t("Refresh")}
		</Button>
	</div>

	{#if previewLoading}
		<div class="flex justify-center py-12"><Spinner /></div>
	{:else}
		<iframe
			title={$t("Newsletter preview")}
			srcdoc={previewSrcdoc}
			class="w-full rounded-lg border"
			style="min-height:500px;"
			sandbox="allow-same-origin"
		></iframe>
	{/if}
</Modal>
